import React, { useEffect, useMemo, useState } from 'react'
import { NextPage, GetServerSideProps, GetServerSidePropsResult } from 'next'
import { useRouter } from 'next/router'
import { FieldErrors } from 'react-hook-form'

import { Button } from '@awazevr/design-system.components.button'
import { Heading, Text } from '@awazevr/design-system.components.text'
import { Box } from '@awazevr/design-system.components.box'
import { Divider } from '@awazevr/design-system.components.divider'
import { Stack } from '@awazevr/design-system.components.stack'
import {
  Label,
  InputControl,
  InputDescription,
} from '@awazevr/design-system.components.input'
import { useForm } from '@awazevr/design-system.components.form'
import { TmsContext } from '@awazevr/mfe-common.contexts.tms'
import { TmsEventType } from '@awazevr/mfe-common.contexts.tms/dist/DataLayer/DataLayer.types'
import {
  ELEMENT_GROUP_SPACING,
  MAIN_HEADING_SIZES,
  PADDING_TOP_MID,
  LOCAL_STORAGE_RETURN_URL_KEY,
} from '../../src/lib/constants'
import {
  EmailInputField,
  PasswordInput,
} from '../../src/components/inputFields'
import TermsOfService from '../../src/components/termsOfService/termsOfService'
import { loginMockedData } from '../../mocks/contentful/loginMockedData'
import {
  selectBrand,
  selectLang,
  selectRemoveBanner,
  selectSetBanner,
  selectSetEmail,
} from '../../src/store/selectors'
import { hash, updateDataLayer } from '../../src/lib/tms'
import { CustomHead } from '../../src/components/custom-head/custom-head'
import { Loader } from '../../src/components/loader'
import { banners } from '../../src/store/banner/constant'
import { ContentfulData, getContentfulData } from '../../utils/contentful'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { usePBStore } from '../../src/store/provider'
import { LogInErrors } from '../../src/types/loginErrors'
import { ButtonLinkStyled } from '../../src/components/buttonLinkStyled/buttonLinkStyled'
import { withServerSideProps } from '@awazevr/mfe-common.contexts.guest-auth/dist/pages/getServerSideProps'
import { type Provider, GuestAuthenticationService } from '@/lib/gas'
import { ExternalLoginProviders } from '@/components/externalLoginProviders/externalLoginProviders'
import { OrComponent } from '@/components/orComponent/orComponent'
import {
  GetServerSidePropsContextWithOptimizelyfeatureFlags,
  withServerSideOptimizely,
} from '@/lib/optimizely/withServerSideProps'
import type { Session } from '@awazevr/mfe-common.contexts.guest-auth'
import { isValidReturnUrl } from '@/lib/is-valid-return-url'
import NextLink from 'next/link'
import { getQueryParamAsString } from '@/lib/query-param-util'

type LoginFormSchemaType = {
  emailAddress: string
  password: string
}

const loginSchema = (emailErrorMessage: string, passwordErrorMessage: string) =>
  z.object({
    emailAddress: z.string().email(emailErrorMessage),
    password: z
      .string({
        description: passwordErrorMessage,
        required_error: passwordErrorMessage,
      })
      .min(1, passwordErrorMessage),
  })

const Index: NextPage<PageProps> = ({
  email,
  uriBooking,
  loginData,
  providers,
  returnUrl,
}) => {
  const router = useRouter()
  const brand = usePBStore(selectBrand)
  const locale = usePBStore(selectLang)
  const providerLabels: Record<(typeof providers)[number]['type'], string> = {
    google: loginData.labels['google_sign_in_button'] ?? 'Sign_In_With_Google',
  }
  const localeProviders = providers.map((provider) => {
    return {
      ...provider,
      label: providerLabels[provider.type],
    }
  })

  const { labels, inputs, links } = loginData

  const emailErrorMessage =
    inputs['input_email']?.message ??
    'Check your email address, including formatting'
  const passwordErrorMessage =
    inputs['input_pass']?.required ?? 'Check your password'
  const formHooks = useForm<LoginFormSchemaType>({
    resolver: zodResolver(loginSchema(emailErrorMessage, passwordErrorMessage)),
  })

  const loginWithoutAccountLegacyLink = useMemo(() => {
    let link = 'https://mybooking.novasol.com'
    if (locale === 'de-DE') {
      link = 'https://mybooking.novasol.com/280?reservationNumber='
    } else if (locale === 'da-DK') {
      link = 'https://mybooking.novasol.com/208?reservationNumber='
    }
    return link
  }, [locale])

  const removeBanner = usePBStore(selectRemoveBanner)
  const setBanner = usePBStore(selectSetBanner)
  const setEmail = usePBStore(selectSetEmail)
  const tmsContext = React.useContext(TmsContext)
  const [loading, setLoading] = useState(false)
  const [serverErrorOccurred, setServerErrorOccurred] = useState(false)

  const [isButtonDisabled, setIsButtonDisabled] = useState(true)

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsButtonDisabled(false)
  }, [])

  useEffect(() => {
    // if a return url is provided in the url query, store it in session storage
    // that way, if the user needs to create an account first,
    // it can be retrieved when the user logs in
    if (returnUrl) {
      try {
        localStorage?.setItem(LOCAL_STORAGE_RETURN_URL_KEY, returnUrl)
      } catch (err) {
        console.error(err)
        // Local storage not available
      }
    }
  }, [returnUrl])

  if (email) {
    formHooks.setValue('emailAddress', email)
  }

  const onFormValidationError = (errors: FieldErrors<LoginFormSchemaType>) => {
    if (errors.emailAddress || errors.password) {
      const errorField = errors.emailAddress ? 'emailAddress' : 'password'
      updateDataLayer(tmsContext, TmsEventType.link, 'login-validation fail', {
        login_validation_fail:
          errors.emailAddress && errors.password ? 'both' : errorField,
      })
    }
  }

  // After failed login: Remove warnings on change
  const removeErrorMessages = async () => {
    if (serverErrorOccurred) {
      formHooks.clearErrors()
      removeBanner()
    }
    setServerErrorOccurred(false)
  }

  const onSubmit = async () => {
    setLoading(true)
    setServerErrorOccurred(false)
    updateDataLayer(tmsContext, TmsEventType.link, 'login-submit', {})

    const response = await fetch('../api/login-route', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-awaze-brand': `${brand}`,
      },
      body: JSON.stringify(formHooks.getValues()),
    })

    const body = await response.json()

    if (response.status !== 200) {
      setServerErrorOccurred(true)
      updateDataLayer(tmsContext, TmsEventType.link, 'login-fail', {})

      setLoading(false)

      // Set banners with errors
      switch (body.error) {
        case LogInErrors.InternalServerError:
          updateDataLayer(tmsContext, TmsEventType.link, 'login-error', {
            error_description: banners().UnknownErrorHappened.text,
          })
          setBanner(
            'UnknownErrorHappened',
            labels['label_unknown_error_happened'],
          )
          break

        case LogInErrors.UnconfirmedUser:
          setEmail(formHooks.getValues('emailAddress'))
          setBanner(
            'AccountIsNotVerified',
            labels['label_account_is_not_verified'],
            `/sign-up/confirmation`,
          )
          break

        default:
          setBanner(
            'EmailPasswordMisMatch',
            labels['label_email_password_mismatch'],
          )
      }

      // Set validation errors
      formHooks.setError('emailAddress', {
        type: 'InternalServerError',
        message: emailErrorMessage,
      })
      formHooks.setError('password', {
        type: 'InternalServerError',
        message: passwordErrorMessage,
      })
    } else {
      const hashedEmail = await hash(formHooks.getValues().emailAddress)
      updateDataLayer(tmsContext, TmsEventType.link, 'login-complete', {
        user_email_hash: hashedEmail,
      })

      let url = uriBooking
      try {
        const returnUrlStored = localStorage?.getItem(
          LOCAL_STORAGE_RETURN_URL_KEY,
        )
        localStorage?.removeItem(LOCAL_STORAGE_RETURN_URL_KEY)

        if (isValidReturnUrl(returnUrlStored)) {
          url = returnUrlStored
        }
      } catch (err) {
        console.error(err)
        // Session storage not available
      }

      globalThis.location.replace(url)
    }
  }

  return (
    <Box>
      <CustomHead
        brand={brand}
        pageName={labels['seo_page_name'] ?? ''}
        content={labels['seo_page_description'] ?? ''}
      />
      <form onSubmit={formHooks.handleSubmit(onSubmit, onFormValidationError)}>
        <Stack spacing={ELEMENT_GROUP_SPACING}>
          <Stack isHorizontal align="center" justify="space-between">
            <Heading sizes={MAIN_HEADING_SIZES}>
              {labels['label_login']}
            </Heading>
          </Stack>

          {/* E M A I L  */}

          <InputControl>
            <Label htmlFor="emailAddress">{inputs['input_email']?.label}</Label>

            <EmailInputField
              formHooks={formHooks}
              onKeyDown={removeErrorMessages}
              placeholder={inputs['input_email']?.placeholder}
            />
          </InputControl>

          {/* P A S S W O R D */}

          <InputControl>
            <Label htmlFor="password">{inputs['input_pass']?.label}</Label>
            <PasswordInput
              formHooks={formHooks}
              iconPlaceholder={inputs['input_pass']?.iconTitle}
              onKeyDown={removeErrorMessages}
              placeholder={inputs['input_pass']?.placeholder}
            />
            <InputDescription>
              {labels['forgot_your_password'] ?? 'Forgot your password?'}{' '}
              <ButtonLinkStyled
                text={links['reset_password']?.title ?? 'Reset it'}
                type="button"
                onClick={() => {
                  router.push('/forgotten-password')
                }}
                data-testid="reset-password-button-link"
                colourScheme="brand-secondary"
                variant="text"
                size="small"
              />
            </InputDescription>
          </InputControl>

          {/* S U B M I T   B T N */}
          <Button
            icon={loading ? <Loader /> : undefined}
            text={loading ? '' : (labels['btn_log_in'] ?? 'Log in')}
            type="submit"
            data-testid="login-button"
            colourScheme="brand-primary"
            isDisabled={isButtonDisabled || loading}
          />

          {/* E X T E R N A L  L O G I N */}

          {providers?.length > 0 && (
            <>
              <OrComponent label={labels['label_or_divider'] as string} />
              <ExternalLoginProviders providers={localeProviders} />
            </>
          )}

          {/* O T H E R   L O G I N */}
          <Stack align="center">
            <Text sizes={300} as={'span'}>
              {labels['label_no_account']}{' '}
              <ButtonLinkStyled
                text={links['link_add_account']?.title ?? 'Create account'}
                type="button"
                onClick={() => {
                  updateDataLayer(
                    tmsContext,
                    TmsEventType.link,
                    'signup-click',
                    {},
                  )
                  router.push('/sign-up')
                }}
                data-testid="signup-button-link"
                colourScheme="brand-secondary"
                variant="text"
              />
            </Text>
          </Stack>

          {brand === 'novasol' && ['de-DE', 'da-DK'].includes(locale) && (
            <Stack align="center">
              <NextLink href={loginWithoutAccountLegacyLink}>
                <Button
                  text={
                    labels['label_login_without_account'] ??
                    'Continue without an account'
                  }
                  variant="text"
                  colourScheme="brand-primary"
                  data-testid="booking-without-account-button"
                />
              </NextLink>
            </Stack>
          )}
          <Box padding={PADDING_TOP_MID}>
            <Divider margin={0} />
          </Box>
          <TermsOfService data={loginData} />
        </Stack>
      </form>
    </Box>
  )
}

type PageProps = {
  email: string | null
  uriBooking: string
  loginData: ContentfulData
  providers: Provider[]
  returnUrl: string | null
}

type CallbackProps = GetServerSidePropsContextWithOptimizelyfeatureFlags & {
  session?: Session
}
export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const callback = async ({
    optimizely,
    session,
    req,
    query,
    locale = 'en-GB',
  }: CallbackProps): Promise<GetServerSidePropsResult<PageProps>> => {
    const { headers } = req
    const { email, returnurl, returnUrl } = query ?? {}
    const parsedEmail = getQueryParamAsString(email)
    // we support both returnurl and returnUrl for ease of use
    const parsedReturnUrl = getQueryParamAsString(returnurl ?? returnUrl)
    const brand = (headers['x-awaze-brand'] as string) ?? 'awaze'
    const bookingsPath = `/bookings/${locale}`
    const gasService = new GuestAuthenticationService(
      process.env.GAS_BASE_URI as string,
      brand,
      req.cookies['awaze_correlation_id'] ?? '',
    )

    if (session?.user) {
      return {
        redirect: {
          destination: bookingsPath,
          basePath: false,
          permanent: true,
        },
      }
    }

    const loginData = await getContentfulData(
      brand,
      locale,
      '/',
      loginMockedData,
    )

    let providers: Provider[] = []
    if (
      optimizely?.isFeatureFlagEnabled(
        'guest_engagement-auth_mfe-google_login',
        false,
      )
    ) {
      const res = await gasService.getProviders()
      if (!res.hasErrors && res.body) {
        providers = res.body.providers
      } else {
        console.error('Error fetching providers:', res.errors)
      }
    }

    return {
      props: {
        email: parsedEmail,
        uriBooking: bookingsPath,
        returnUrl: parsedReturnUrl,
        loginData,
        providers,
      },
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return withServerSideOptimizely(withServerSideProps(callback as any) as any)(
    ctx,
  )
}

export default Index
